import {
  RouteDriverSwapDTO,
  RouteFieldTesterSuggestionDTO,
  RoutePackageDTO,
  RoutePlanActionResultDTO,
  RoutePlanProposalDTO,
  RoutePlanUnplannableReasonEnum,
  RouteRebookingKindEnum,
  RouteRebookingProposalDTO,
} from 'api/model';
import useUsers from 'contexts/basicData/useUsers';
import {
  useRoutePlanningApply,
  useRoutePlanningConfirmRebooking,
  useRoutePlanningCounties,
  useRoutePlanningDeclineRebooking,
  useRoutePlanningEvaluatePackage,
  useRoutePlanningPreview,
  useRoutePlanningReserveForManualPlanning,
} from 'api/route-planning/route-planning';
import BetaBadge from 'components/BetaBadge';
import BusinessUnitSelect from 'components/inputs/BusinessUnitSelect';
import Button from 'components/inputs/Button';
import MultiSelect, { Option } from 'components/inputs/MultiSelect';
import Select from 'components/inputs/Select';
import ConfirmModal from 'components/Modal/ConfirmModal';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import useModalStack from 'contexts/modal/useModalStack';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import useBusinessUnits from 'contexts/basicData/useBusinessUnits';
import useHasAccessToRoutePlanApply from 'hooks/useHasAccessToRoutePlanApply';
import ApplyBar from './ApplyBar';
import FeedbackButton from './FeedbackButton';
import FeedbackModal from './FeedbackModal';
import { keepCandidateChoice } from './candidate-choice-helpers';
import { buildFeedbackSnapshot, describeError } from './feedback-helpers';
import FieldTesterSwitchModal from './FieldTesterSwitchModal';
import UnplannableTable, { ReserveTarget } from './UnplannableTable';
import PackageCard, { PackageApplyState } from './PackageCard';
import { ConfirmedRebooking, RebookingAnswer } from './rebooking-helpers';
import ReserveAssignmentModal from './ReserveAssignmentModal';
import RouteMap from './RouteMap';
import MissingPriceNotice from './MissingPriceNotice';
import RunOverview from './RunOverview';
import {
  buildPackageActions,
  buildRideAlongCarriers,
  countSelectedAnnonsByDestination,
  defaultSelection,
  describeHomeRideStatus,
  flattenPlanActions,
  formatClock,
  GapChoice,
  getSelectedCandidate,
  groupCarDeparture,
  groupPackagesForDisplay,
  isRideAlongCarrierReady,
  PackageActionPlan,
  PackageSelection,
  partitionByPlanningClass,
  summarizeActions,
} from './selection-helpers';
import StatsBar from './StatsBar';

type SortMode = 'start' | 'assignments';

/**
 * Förslag A: automatiken tar de enkla fallen, allt annat hamnar i en lista någon får titta på.
 *
 * Etiketterna heter "Redo att tillämpa" och "Behöver input" och inte "Automatiskt"/"Manuellt":
 * ordet automatiskt läser som att systemet redan har bestämt, fast ingenting skrivs förrän
 * trafikledaren verkställer. Nycklarna är kvar som auto/manual — de speglar backendens
 * PlanningClass.
 */
type PlanningTab = 'auto' | 'manual';

const SORT_OPTIONS: { label: string; value: SortMode }[] = [
  { label: 'Tidigast start', value: 'start' },
  { label: 'Flest uppdrag', value: 'assignments' },
];

/**
 * Anställningsform. Heltidare och timanställda planeras olika — timmarna kostar bara när de
 * används, heltiden är redan betald — så trafikledaren vill kunna köra ett förslag mot enbart
 * den ena gruppen. Filtret gäller urvalet planeraren får använda, inte bara listan i rullgardinen.
 */
type EmploymentFilter = 'all' | 'fullTime' | 'hourly';

const EMPLOYMENT_OPTIONS: { label: string; value: EmploymentFilter }[] = [
  { label: 'Alla', value: 'all' },
  { label: 'Heltid', value: 'fullTime' },
  { label: 'Timanställda', value: 'hourly' },
];

/**
 * Sorterar enbart visningsordningen. Allt tillstånd (markeringar, apply-status, skiftespar) nycklas
 * på packageIndex och inte på listposition, så ordningen är fri att ändra.
 */
const sortPackages = (
  packages: RoutePackageDTO[],
  mode: SortMode
): RoutePackageDTO[] => {
  if (mode === 'start') return packages;
  return [...packages].sort(
    (a, b) => b.legs.length - a.legs.length || a.packageIndex - b.packageIndex
  );
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 10px 16px 0;
`;

const BetaNotice = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.85em;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 14px;
`;

const Control = styled.label`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 0.8em;
  color: rgb(120, 120, 120);

  input[type='date'] {
    font: inherit;
    font-size: 1.15em;
    padding: 6px 8px;
    border: 1px solid rgb(190, 190, 190);
    border-radius: 4px;
  }
`;

const GeneratedInfo = styled.span`
  margin-left: auto;
  font-size: 0.85em;
  color: rgb(120, 120, 120);
`;

/** Under den här fönsterbredden ryms inte kort och karta bredvid varandra. */
const SIDE_BY_SIDE_MIN_PX = 1200;

/**
 * Kort och karta bredvid varandra när det finns plats, annars kartan överst. minmax(0, …) och
 * min-width på barnen: utan dem växte kortkolumnen till rubrikens fulla bredd och tryckte ut
 * både kortet och kartan till höger.
 */
const ContentGrid = styled.div<{ showMap: boolean }>`
  display: grid;
  grid-template-columns: ${({ showMap }) =>
    showMap ? 'minmax(0, 7fr) minmax(320px, 5fr)' : 'minmax(0, 1fr)'};
  gap: 14px;
  align-items: start;

  & > * {
    min-width: 0;
  }

  @media (max-width: ${SIDE_BY_SIDE_MIN_PX - 1}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** Smal skärm: kartan läggs ovanför korten och följer inte med vid scroll. */
const MapColumn = styled.div`
  min-width: 0;
  /* Hela radens höjd, så att kartans position: sticky har något att följa med i. */
  align-self: stretch;

  @media (max-width: ${SIDE_BY_SIDE_MIN_PX - 1}px) {
    order: -1;

    & > * {
      position: static;
    }
  }
`;

const TabsSpacer = styled.span`
  flex: 1;
`;

const MapToggle = styled.button`
  align-self: center;
  border: none;
  background: none;
  padding: 0 4px;
  font-size: 12px;
  color: rgb(70, 70, 70);
  text-decoration: underline;
  cursor: pointer;
`;

const PackageList = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/**
 * Ramen som håller ett skiftespar samman. Paren verkställs tillsammans — ett halvt verkställt
 * skifte lämnar en förare vid bytespunkten utan bil hem — så de får inte läsas som två
 * oberoende paket i listan.
 */
const SwapGroup = styled.div`
  display: flex;
  gap: 0;
`;

const SwapBracket = styled.div`
  width: 24px;
  flex: none;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 14px;
    bottom: 14px;
    width: 2px;
    background: rgb(120, 160, 210);
  }
`;

const SwapBracketMark = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 3px 0;
  font-size: 14px;
  color: rgb(30, 90, 160);
  background: rgb(255, 255, 255);
`;

const SwapGroupBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SwapGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 2px;
  font-size: 12px;
  color: rgb(30, 90, 160);
`;

const SwapGroupTitle = styled.span`
  font-weight: 600;
`;

const SwapGroupSpacer = styled.span`
  margin-left: auto;
`;

const CollapseAllButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: rgb(70, 70, 70);
  text-decoration: underline;
  cursor: pointer;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid rgb(190, 190, 190);
`;

const Tab = styled.button<{ active?: boolean }>`
  border: none;
  background: none;
  padding: 8px 14px;
  margin-bottom: -1px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  color: ${({ active }) =>
    active ? 'rgb(30, 90, 160)' : 'rgb(110, 110, 110)'};
  border-bottom: 2px solid
    ${({ active }) => (active ? 'rgb(30, 90, 160)' : 'transparent')};

  span {
    margin-left: 6px;
    font-family: monospace;
    font-size: 0.9em;
    font-weight: 400;
  }
`;

const CenteredState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 60px 0;
  color: rgb(120, 120, 120);
`;

const tomorrowAsInputValue = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

const RuttplaneringPage: React.FC = () => {
  const modalStack = useModalStack();
  const queryClient = useQueryClient();
  const canApply = useHasAccessToRoutePlanApply();

  const [dateValue, setDateValue] = useState<string>(tomorrowAsInputValue());
  const [businessUnits, setBusinessUnits] = useState<Set<number>>(new Set());
  const [counties, setCounties] = useState<Set<number>>(new Set());
  const [fieldTesterFilter, setFieldTesterFilter] = useState<Set<number>>(
    new Set()
  );
  const [employment, setEmployment] = useState<EmploymentFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('start');
  const [proposal, setProposal] = useState<RoutePlanProposalDTO | null>(null);
  const [selections, setSelections] = useState<
    Record<number, PackageSelection>
  >({});
  const [applyStates, setApplyStates] = useState<
    Record<number, PackageApplyState>
  >({});
  const [hoveredPackage, setHoveredPackage] = useState<number | null>(null);
  const [generatedInfo, setGeneratedInfo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PlanningTab>('auto');
  const [showOverview, setShowOverview] = useState(false);
  // Kartan tar halva bredden. På en smal skärm är korten viktigare, så där börjar den dold.
  const [showMap, setShowMap] = useState<boolean>(
    () => window.innerWidth >= SIDE_BY_SIDE_MIN_PX
  );
  // Betyg som skickats i den här körningen, per paket. Nollställs när ett nytt förslag genereras
  // — paketindexen pekar då på andra paket.
  const [packageRatings, setPackageRatings] = useState<Record<number, number>>(
    {}
  );

  const previewMutation = useRoutePlanningPreview();
  const applyMutation = useRoutePlanningApply();
  const reserveMutation = useRoutePlanningReserveForManualPlanning();
  const confirmRebookingMutation = useRoutePlanningConfirmRebooking();
  const declineRebookingMutation = useRoutePlanningDeclineRebooking();
  const evaluatePackageMutation = useRoutePlanningEvaluatePackage();
  // Uppdraget vars ombokningssvar just nu skickas.
  const [rebookingBusy, setRebookingBusy] = useState<number | undefined>();
  // Ja som är sparade i den här körningen. Tiden står redan i databasen och omräkningen bär
  // inga förslag, så utan detta försvann benets "bekräftad" i samma stund som svaret sparades.
  const [confirmedRebookings, setConfirmedRebookings] = useState<
    Record<number, ConfirmedRebooking>
  >({});
  const { data: countyOptionsData } = useRoutePlanningCounties();

  const countyOptions = useMemo(
    (): Option<number>[] =>
      (countyOptionsData ?? []).map((c) => ({
        label: c.areaName,
        value: c.id,
      })),
    [countyOptionsData]
  );

  const users = useUsers();
  const { unitsById } = useBusinessUnits();

  /**
   * Fälttestare som över huvud taget får planeras: aktiva, körbara och av vald anställningsform.
   * Både rullgardinen och det som skickas till planeraren utgår från den här listan, så de kan
   * inte glida isär.
   */
  const selectableFieldTesters = useMemo(
    () =>
      Object.values(users)
        .filter((u) => u.isActive && u.isAvailableForDriving)
        .filter((u) => {
          if (employment === 'fullTime') return u.isFullTime;
          if (employment === 'hourly') return !u.isFullTime;
          return true;
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'sv')),
    [users, employment]
  );

  const fieldTesterOptions = useMemo(
    (): Option<number>[] =>
      selectableFieldTesters.map((u) => ({ label: u.name, value: u.userID })),
    [selectableFieldTesters]
  );

  const generate = useCallback(async () => {
    const startedAt = Date.now();
    try {
      const result = await previewMutation.mutateAsync({
        data: {
          date: new Date(`${dateValue}T00:00:00`),
          businessUnitIds:
            businessUnits.size > 0 ? Array.from(businessUnits) : undefined,
          countyIds: counties.size > 0 ? Array.from(counties) : undefined,
          // Ett uttryckligt val vinner. Utan val men med anställningsform skickas hela den
          // gruppen, annars hade filtret bara krympt rullgardinen och planeraren ändå fått
          // välja fritt bland alla.
          includedFieldTesterIds:
            fieldTesterFilter.size > 0
              ? Array.from(fieldTesterFilter)
              : employment !== 'all'
                ? selectableFieldTesters.map((u) => u.userID)
                : undefined,
        },
      });
      setProposal(result);
      setConfirmedRebookings({});
      setSelections(
        Object.fromEntries(
          result.packages.map((pkg) => [
            pkg.packageIndex,
            defaultSelection(pkg),
          ])
        )
      );
      setApplyStates({});
      setPackageRatings({});
      setActiveTab('auto');
      setShowOverview(false);
      const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
      const clock = new Date().toLocaleTimeString('sv-SE', {
        hour: '2-digit',
        minute: '2-digit',
      });
      setGeneratedInfo(`Senast genererat ${clock} · ${seconds} sekunder`);
    } catch {
      toast.error('Kunde inte generera förslag — försök igen.');
    }
  }, [
    businessUnits,
    counties,
    dateValue,
    employment,
    fieldTesterFilter,
    previewMutation,
    selectableFieldTesters,
  ]);

  /** Paketindex → skiftestransporten paketet ingår i. Båda sidorna pekar på samma post. */
  const swapByPackageIndex = useMemo(() => {
    const map: Record<number, RouteDriverSwapDTO> = {};
    (proposal?.driverSwaps ?? []).forEach((swap) => {
      map[swap.sideA.packageIndex] = swap;
      map[swap.sideB.packageIndex] = swap;
    });
    return map;
  }, [proposal]);

  const updateSelection = useCallback(
    (packageIndex: number, update: Partial<PackageSelection>) => {
      setSelections((prev) => {
        const next = {
          ...prev,
          [packageIndex]: { ...prev[packageIndex], ...update },
        };

        // Halva ett skiftespar går inte att verkställa — då står en förare vid bytespunkten
        // utan bil hem. Markeringen följer därför med till motparten.
        const swap = swapByPackageIndex[packageIndex];
        if (swap && update.includePackage !== undefined) {
          const partnerIndex =
            packageIndex === swap.sideA.packageIndex
              ? swap.sideB.packageIndex
              : swap.sideA.packageIndex;
          next[partnerIndex] = {
            ...prev[partnerIndex],
            includePackage: update.includePackage,
          };
        }

        return next;
      });
    },
    [swapByPackageIndex]
  );

  const openSwitchModal = useCallback(
    (pkg: RoutePackageDTO) => {
      if (!proposal) return;
      const currentSelection = selections[pkg.packageIndex];
      modalStack.push(
        <FieldTesterSwitchModal
          currentUserId={currentSelection?.candidateUserId ?? null}
          onClose={() => modalStack.pop()}
          rideAlongCarriers={buildRideAlongCarriers(
            proposal.packages,
            selections,
            pkg.packageIndex
          )}
          onPicked={(candidate: RouteFieldTesterSuggestionDTO) => {
            modalStack.pop();
            updateSelection(pkg.packageIndex, {
              candidateUserId: candidate.userId,
              manualCandidate: candidate.rank === 0 ? candidate : undefined,
              firstGapChoice: 'none',
              lastGapChoice: 'none',
              // Överföringarnas förslag är fälttestarberoende (annonsbudget, behörighet), så en
              // ny förare gör de gamla valen ogiltiga — samma skäl som luckorna nollställs.
              transferGapChoices: {},
              hotelConfirmed: false,
            });
          }}
          pkg={pkg}
          planDate={new Date(proposal.date)}
        />
      );
    },
    [modalStack, proposal, selections, updateSelection]
  );

  /**
   * Svaret på ett ombokningsförslag. Tiden flyttas (eller nejet loggas) på servern, och paketen
   * räknas sedan om mot tiderna som står i databasen:
   * - Ja / Annan tid: samma paket, nu på den bekräftade tiden.
   * - Nej på en fylld kort dag: mottagarens och givarens paket var för sig, som före förslaget.
   * - Nej på en håltimme: paketet gällde redan som det var — bara förslaget tas bort.
   */
  const answerRebooking = useCallback(
    async (
      pkg: RoutePackageDTO,
      rebooking: RouteRebookingProposalDTO,
      answer: RebookingAnswer
    ) => {
      if (!proposal) return;
      const date = new Date(proposal.date);
      // Varför en vald fälttestare föll vid omräkningen — visas efteråt, annars står det bara
      // "Ingen fälttestare kan ta det" på kortet.
      const candidateFailures: string[] = [];
      const evaluate = async (
        assignmentIds: number[],
        fieldTesterId: number | null | undefined,
        packageIndex: number
      ): Promise<RoutePackageDTO> => {
        const own = new Set(assignmentIds);
        const result = await evaluatePackageMutation.mutateAsync({
          data: {
            date,
            packageIndex,
            assignmentIds,
            fieldTesterId: fieldTesterId ?? undefined,
            // Förslagets övriga uppdrag är oplanerade i databasen — de får inte föreslås som
            // utfyllnad i det omräknade paketets luckor.
            otherPackageAssignmentIds: proposal.packages
              .flatMap((p) => p.legs.map((l) => l.assignmentId))
              .filter((id) => !own.has(id)),
          },
        });
        if (result.candidateFailureReason) {
          candidateFailures.push(result.candidateFailureReason);
        }
        return result.package;
      };

      setRebookingBusy(rebooking.assignmentId);
      try {
        if (answer.kind === 'confirm') {
          const saved = await confirmRebookingMutation.mutateAsync({
            data: {
              assignmentId: rebooking.assignmentId,
              newBookedTo: answer.newBookedTo,
              proposedBookedTo: rebooking.proposedBookedTo,
            },
          });
          setConfirmedRebookings((prev) => ({
            ...prev,
            [rebooking.assignmentId]: {
              assignmentId: rebooking.assignmentId,
              oldBookedTo: new Date(saved.oldBookedTo),
              newBookedTo: new Date(saved.newBookedTo),
              confirmedAt: new Date(),
            },
          }));
        } else {
          await declineRebookingMutation.mutateAsync({
            data: {
              assignmentId: rebooking.assignmentId,
              proposedBookedTo: rebooking.proposedBookedTo,
            },
          });
        }
      } catch (error) {
        toast.error(
          `Kunde inte spara svaret: ${describeError(error) ?? 'okänt fel'}`
        );
        setRebookingBusy(undefined);
        return;
      }

      let replacements: RoutePackageDTO[];
      try {
        if (answer.kind === 'confirm') {
          const evaluated = await evaluate(
            pkg.legs.map((l) => l.assignmentId),
            rebooking.receiverFieldTesterId,
            pkg.packageIndex
          );
          // Omräkningen bär inga ombokningsförslag. Var det här ena halvan av ett par ska den
          // andra halvan ligga kvar — det samtalet är inte ringt än.
          const pairedStillOpen = (pkg.rebookingProposals ?? []).filter(
            (r) =>
              r.assignmentId === rebooking.pairedAssignmentId &&
              evaluated.legs.some((l) => l.assignmentId === r.assignmentId)
          );
          replacements = [
            {
              ...keepCandidateChoice(evaluated, pkg),
              rebookingProposals: [
                ...(evaluated.rebookingProposals ?? []),
                ...pairedStillOpen,
              ],
            },
          ];
        } else if (rebooking.kind === RouteRebookingKindEnum.ShortenWait) {
          replacements = [
            {
              ...pkg,
              rebookingProposals: (pkg.rebookingProposals ?? []).filter(
                (r) => r.assignmentId !== rebooking.assignmentId
              ),
            },
          ];
        } else {
          const donorIndex =
            rebooking.mergedPackageIndex ??
            Math.max(...(proposal.packages.map((p) => p.packageIndex) ?? [0])) +
              1;
          replacements = [
            await evaluate(
              rebooking.receiverAssignmentIds,
              rebooking.receiverFieldTesterId,
              pkg.packageIndex
            ),
            await evaluate(
              rebooking.donorAssignmentIds,
              rebooking.donorFieldTesterId,
              donorIndex
            ),
          ];
        }
      } catch (error) {
        toast.error(
          `Svaret är sparat, men paketet kunde inte räknas om — generera ett nytt förslag. (${describeError(error) ?? 'okänt fel'})`
        );
        setRebookingBusy(undefined);
        return;
      }

      setProposal((prev) =>
        prev
          ? {
              ...prev,
              packages: prev.packages.flatMap((p) =>
                p.packageIndex === pkg.packageIndex ? replacements : [p]
              ),
            }
          : prev
      );
      setSelections((prev) => ({
        ...prev,
        ...Object.fromEntries(
          replacements.map((p) => [p.packageIndex, defaultSelection(p)])
        ),
      }));
      setApplyStates((prev) => {
        const next = { ...prev };
        replacements.forEach((p) => delete next[p.packageIndex]);
        return next;
      });
      setRebookingBusy(undefined);
      if (candidateFailures.length > 0) {
        toast.error(
          `Paketet saknar nu fälttestare: ${candidateFailures.join(' · ')}`,
          { duration: 10_000 }
        );
      }
      toast.success(
        answer.kind === 'confirm'
          ? `Ny tid sparad för ${rebooking.registrationNumber ?? 'uppdraget'} — paketet är omräknat.`
          : rebooking.kind === RouteRebookingKindEnum.ShortenWait
            ? 'Nejet är loggat — paketet gäller som det var.'
            : 'Nejet är loggat — paketen är delade igen.'
      );
    },
    [
      confirmRebookingMutation,
      declineRebookingMutation,
      evaluatePackageMutation,
      proposal,
    ]
  );

  const setGapChoice = useCallback(
    (packageIndex: number, gap: 'first' | 'last', choice: GapChoice) => {
      updateSelection(
        packageIndex,
        gap === 'first' ? { firstGapChoice: choice } : { lastGapChoice: choice }
      );
    },
    [updateSelection]
  );

  const setTransferGapChoice = useCallback(
    (packageIndex: number, assignmentId: number, choice: GapChoice) => {
      updateSelection(packageIndex, {
        transferGapChoices: {
          ...(selections[packageIndex]?.transferGapChoices ?? {}),
          [assignmentId]: choice,
        },
      });
    },
    [selections, updateSelection]
  );

  const buildPlans = useCallback(
    (packages: RoutePackageDTO[]): PackageActionPlan[] =>
      packages
        .filter((pkg) => applyStates[pkg.packageIndex]?.status !== 'applied')
        .map((pkg) => {
          // Motparten skickas med så att ett skifte inte kan verkställas när ANDRA halvan är
          // blockerad eller avmarkerad — paret verkställs tillsammans eller inte alls.
          const swap = swapByPackageIndex[pkg.packageIndex];
          const partnerIndex = swap
            ? pkg.packageIndex === swap.sideA.packageIndex
              ? swap.sideB.packageIndex
              : swap.sideA.packageIndex
            : undefined;
          const partnerPkg =
            partnerIndex === undefined
              ? undefined
              : proposal?.packages.find((p) => p.packageIndex === partnerIndex);

          // Bärarens paket när en samåkning är vald — spärren behöver veta om bilen finns.
          const selection = selections[pkg.packageIndex];
          const candidate = getSelectedCandidate(pkg, selection);
          const ride =
            (selection?.firstGapChoice === 'rideAlong'
              ? candidate?.firstLegGap?.rideAlong
              : undefined) ??
            (selection?.lastGapChoice === 'rideAlong'
              ? candidate?.lastLegGap?.rideAlong
              : undefined);
          const carrierPkg = ride
            ? proposal?.packages.find(
                (p) => p.packageIndex === ride.carrierPackageIndex
              )
            : undefined;

          return {
            packageIndex: pkg.packageIndex,
            actions: buildPackageActions(
              pkg,
              selection,
              swap,
              partnerPkg && selections[partnerIndex!]
                ? { pkg: partnerPkg, selection: selections[partnerIndex!] }
                : undefined,
              ride === undefined
                ? undefined
                : isRideAlongCarrierReady(
                    ride,
                    carrierPkg,
                    selections[ride.carrierPackageIndex]
                  ),
              groupCarDeparture(
                proposal?.packages ?? [],
                selections,
                candidate?.lastLegGap?.homeGroup?.carAssignmentId
              )
            ),
          };
        })
        .filter((plan) => plan.actions.length > 0),
    [applyStates, proposal, selections, swapByPackageIndex]
  );

  const executePlans = useCallback(
    async (plans: PackageActionPlan[]) => {
      if (!proposal || plans.length === 0) return;

      // Inte en ren flatMap: annonsens förfrågan pekar på sin tillresa med ett index inom
      // paketet, och det måste flyttas till hela begärans index.
      const allActions = flattenPlanActions(plans);
      let results: RoutePlanActionResultDTO[];
      try {
        results = await applyMutation.mutateAsync({
          data: {
            proposalId: proposal.proposalId,
            date: proposal.date,
            actions: allActions,
          },
        });
      } catch {
        toast.error('Verkställan misslyckades — inga rader genomfördes.');
        return;
      }

      // Mappa resultatraderna tillbaka till paketen via actionIndex-intervall.
      let cursor = 0;
      const newStates: Record<number, PackageApplyState> = {};
      plans.forEach((plan) => {
        const packageResults = results.slice(
          cursor,
          cursor + plan.actions.length
        );
        cursor += plan.actions.length;
        const failed = packageResults.filter((r) => !r.success);
        if (failed.length === 0) {
          newStates[plan.packageIndex] = { status: 'applied', skipped: 0 };
        } else if (failed.length < packageResults.length) {
          newStates[plan.packageIndex] = {
            status: 'applied',
            skipped: failed.length,
          };
        } else {
          newStates[plan.packageIndex] = {
            status: 'failed',
            message: failed[0].failureReason ?? 'okänt fel',
          };
        }
      });
      setApplyStates((prev) => ({ ...prev, ...newStates }));

      const successCount = results.filter((r) => r.success).length;
      const failCount = results.length - successCount;
      if (failCount === 0) {
        toast.success(`${successCount} rader verkställda.`);
      } else {
        toast.error(
          `${successCount} rader verkställda, ${failCount} hoppades över.`
        );
      }

      queryClient.invalidateQueries({
        predicate: (query) =>
          JSON.stringify(query.queryKey)
            .toLowerCase()
            .includes('dayplanning') ||
          JSON.stringify(query.queryKey)
            .toLowerCase()
            .includes('internaldelivery'),
      });
    },
    [applyMutation, proposal, queryClient]
  );

  const confirmAndExecute = useCallback(
    (plans: PackageActionPlan[]) => {
      if (!proposal || plans.length === 0) return;
      const summary = summarizeActions(plans);

      modalStack.push(
        <ConfirmModal
          confirmText={`Verkställ ${summary.packageCount} paket`}
          content={
            <div>
              <p>
                Följande verkställs för{' '}
                <b>{new Date(proposal.date).toLocaleDateString('sv-SE')}</b>:
              </p>
              <ul>
                <li>{summary.assignmentCount} uppdrag sätts till Planerad</li>
                {summary.driverSwapCount > 0 && (
                  <li>
                    {summary.driverSwapCount} skiftestransport verkställs —
                    varje uppdrag delas i två ben vid bytespunkten, med en
                    förare per ben
                  </li>
                )}
                {summary.assignmentRequestCount > 0 && (
                  <li>
                    {summary.assignmentRequestCount}{' '}
                    annonsförfrågan/-förfrågningar skapas (budget dras)
                  </li>
                )}
                {summary.personalTransportCount > 0 && (
                  <li>
                    {summary.personalTransportCount} personlig transport skapas
                  </li>
                )}
                {summary.hotelStayoverCount > 0 && (
                  <li>
                    {summary.hotelStayoverCount} uppdrag markeras med
                    hotellövernattning
                  </li>
                )}
              </ul>
              <p>
                Varje rad valideras om mot databasen. Rader som redan tilldelats
                av någon annan hoppas över och rapporteras.
              </p>
            </div>
          }
          onCancel={() => modalStack.pop()}
          onConfirm={() => {
            modalStack.pop();
            executePlans(plans);
          }}
          title="Verkställ ruttplan"
        />
      );
    },
    [executePlans, modalStack, proposal]
  );

  const applyAll = useCallback(() => {
    if (!proposal) return;
    confirmAndExecute(
      buildPlans(
        proposal.packages.filter(
          (p) => selections[p.packageIndex]?.includePackage
        )
      )
    );
  }, [buildPlans, confirmAndExecute, proposal, selections]);

  const applySingle = useCallback(
    (pkg: RoutePackageDTO) => {
      // Ett skiftespaket kan inte verkställas ensamt — motparten måste med i samma anrop.
      const swap = swapByPackageIndex[pkg.packageIndex];
      const partner = swap
        ? proposal?.packages.find(
            (p) =>
              p.packageIndex ===
              (pkg.packageIndex === swap.sideA.packageIndex
                ? swap.sideB.packageIndex
                : swap.sideA.packageIndex)
          )
        : undefined;

      confirmAndExecute(buildPlans(partner ? [pkg, partner] : [pkg]));
    },
    [buildPlans, confirmAndExecute, proposal?.packages, swapByPackageIndex]
  );

  const deselectAll = useCallback(() => {
    setSelections((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([key, sel]) => [
          key,
          { ...sel, includePackage: false },
        ])
      )
    );
  }, []);

  // Live: hur många annonser per destination som är VALDA just nu — visas på varje annonsrad.
  const sortedPackages = useMemo(
    () => (proposal ? sortPackages(proposal.packages, sortMode) : []),
    [proposal, sortMode]
  );

  // Kollapsläge per paket. Tomt = allt utfällt, vilket är default: lucka-valen och
  // hotellbekräftelsen är det trafikledningen faktiskt ska ta ställning till, och de syns bara
  // i utfällt läge.
  const [collapsedPackages, setCollapsedPackages] = useState<
    Record<number, boolean>
  >({});

  const togglePackageCollapsed = useCallback((packageIndex: number) => {
    setCollapsedPackages((prev) => ({
      ...prev,
      [packageIndex]: !prev[packageIndex],
    }));
  }, []);

  const partition = useMemo(
    () => partitionByPlanningClass(sortedPackages, swapByPackageIndex),
    [sortedPackages, swapByPackageIndex]
  );

  // Paketen i den flik som visas. Båda flikarna ritar samma kort — se renderingen längre ned.
  const visiblePackages =
    activeTab === 'auto' ? partition.auto : partition.manual;

  // Skiftespar blir EN rad med två kort i, resten ett kort per rad.
  const packageRows = useMemo(
    () => groupPackagesForDisplay(visiblePackages, swapByPackageIndex),
    [visiblePackages, swapByPackageIndex]
  );

  // Räknas på den SYNLIGA listan: knappen sitter över korten i en flik, och mätt på alla paket
  // stod den kvar på "Kollapsa alla" efter att planeraren fällt ihop allt hon kunde se — för att
  // den andra flikens kort låg utfällda.
  const allCollapsed =
    visiblePackages.length > 0 &&
    visiblePackages.every((pkg) => collapsedPackages[pkg.packageIndex]);

  const toggleAllCollapsed = useCallback(() => {
    setCollapsedPackages((prev) => {
      const everyCollapsed =
        visiblePackages.length > 0 &&
        visiblePackages.every((pkg) => prev[pkg.packageIndex]);
      return {
        ...prev,
        ...Object.fromEntries(
          visiblePackages.map((pkg) => [pkg.packageIndex, !everyCollapsed])
        ),
      };
    });
  }, [visiblePackages]);

  const selectedAnnonsByDestination = useMemo(
    () =>
      proposal
        ? countSelectedAnnonsByDestination(proposal.packages, selections)
        : {},
    [proposal, selections]
  );

  const applySummary = useMemo(() => {
    if (!proposal) return null;
    return summarizeActions(
      buildPlans(
        proposal.packages.filter(
          (p) => selections[p.packageIndex]?.includePackage
        )
      )
    );
  }, [buildPlans, proposal, selections]);

  /**
   * Låser eller släpper ett uppdrag och kör om förhandsvisningen. Omkörningen är poängen: låset
   * ändrar poolen, och en lista som visar gårdagens paketering efter att poolen ändrats är
   * felaktig på ett sätt som inte syns.
   */
  const reserveAssignment = useCallback(
    (target: ReserveTarget) => {
      modalStack.push(
        <ReserveAssignmentModal
          currentNote={target.currentNote}
          onCancel={() => modalStack.pop()}
          onConfirm={async (note) => {
            modalStack.pop();
            try {
              await reserveMutation.mutateAsync({
                data: {
                  assignmentId: target.assignmentId,
                  reserved: target.reserved,
                  note,
                },
              });
            } catch {
              toast.error(
                target.reserved
                  ? 'Kunde inte låsa uppdraget — planerade uppdrag går inte att låsa.'
                  : 'Kunde inte släppa uppdraget.'
              );
              return;
            }
            toast.success(
              target.reserved ? 'Uppdraget är låst.' : 'Uppdraget är släppt.'
            );
            generate();
          }}
          registrationNumber={target.registrationNumber}
          reserved={target.reserved}
          route={target.route}
        />
      );
    },
    [generate, modalStack, reserveMutation]
  );

  const isGenerating = previewMutation.isLoading;
  const isApplying = applyMutation.isLoading;

  /**
   * Feedbacken tar en ögonblicksbild av ALLT vyn vet — filter, förslaget, markeringarna, vad som
   * verkställts och vad verkställ skulle skicka nu — så att den som läser den kan se exakt vad
   * trafikledaren såg när hon reagerade.
   */
  const openFeedback = useCallback(
    (ratingPackageIndex?: number) => {
      const snapshot = buildFeedbackSnapshot({
        filters: {
          date: dateValue,
          businessUnits: Array.from(businessUnits).map((id) => ({
            id,
            name: unitsById.get(id)?.name,
          })),
          counties: Array.from(counties).map((id) => ({
            id,
            name: countyOptions.find((o) => o.value === id)?.label,
          })),
          fieldTesters: Array.from(fieldTesterFilter).map((id) => ({
            id,
            name: users[id]?.name,
          })),
          employment,
        },
        view: {
          activeTab,
          sortMode,
          showOverview,
          generatedInfo,
          hoveredPackage,
          isGenerating,
          isApplying,
          autoPackageIndexes: partition.auto.map((p) => p.packageIndex),
          manualPackageIndexes: partition.manual.map((p) => p.packageIndex),
          collapsedPackages,
        },
        proposal,
        selections,
        applyStates,
        applySummary,
        pendingPlans: proposal
          ? buildPlans(
              proposal.packages.filter(
                (p) => selections[p.packageIndex]?.includePackage
              )
            )
          : [],
        errors: {
          preview: describeError(previewMutation.error),
          apply: describeError(applyMutation.error),
        },
      });

      modalStack.push(
        <FeedbackModal
          onClose={() => modalStack.pop()}
          onSent={(rating) => {
            if (ratingPackageIndex === undefined || rating === undefined)
              return;
            setPackageRatings((prev) => ({
              ...prev,
              [ratingPackageIndex]: rating,
            }));
          }}
          ratingPackageIndex={ratingPackageIndex}
          snapshot={snapshot}
        />
      );
    },
    [
      activeTab,
      applyMutation.error,
      applyStates,
      applySummary,
      buildPlans,
      businessUnits,
      collapsedPackages,
      counties,
      countyOptions,
      dateValue,
      employment,
      fieldTesterFilter,
      generatedInfo,
      hoveredPackage,
      isApplying,
      isGenerating,
      modalStack,
      partition,
      previewMutation.error,
      proposal,
      selections,
      showOverview,
      sortMode,
      unitsById,
      users,
    ]
  );

  const renderPackageCard = (pkg: RoutePackageDTO) => (
    <PackageCard
      applyState={applyStates[pkg.packageIndex] ?? { status: 'idle' }}
      collapsed={!!collapsedPackages[pkg.packageIndex]}
      driverSwap={swapByPackageIndex[pkg.packageIndex]}
      givenRating={packageRatings[pkg.packageIndex]}
      onRate={() => openFeedback(pkg.packageIndex)}
      followsManualSwapPartner={
        activeTab === 'manual' && pkg.manualReasons.length === 0
      }
      isApplying={isApplying}
      key={pkg.packageIndex}
      onApplySingle={() => applySingle(pkg)}
      onReserveLeg={(leg) =>
        reserveAssignment({
          assignmentId: leg.assignmentId,
          registrationNumber: leg.registrationNumber,
          route: `${leg.fromCity} → ${leg.toCity}`,
          reserved: true,
        })
      }
      onSwitchFieldTester={() => openSwitchModal(pkg)}
      homeRideNotice={describeHomeRideStatus(
        pkg,
        selections[pkg.packageIndex] ?? defaultSelection(pkg),
        proposal?.packages ?? [],
        selections
      )}
      onRebookingAnswer={(rebooking, answer) =>
        answerRebooking(pkg, rebooking, answer)
      }
      rebookingBusyAssignmentId={rebookingBusy}
      confirmedRebookings={confirmedRebookings}
      onToggleCollapsed={() => togglePackageCollapsed(pkg.packageIndex)}
      planDate={new Date(proposal?.date ?? Date.now())}
      selectedAnnonsByDestination={selectedAnnonsByDestination}
      onGapChoice={(gap, choice) => setGapChoice(pkg.packageIndex, gap, choice)}
      onTransferGapChoice={(assignmentId, choice) =>
        setTransferGapChoice(pkg.packageIndex, assignmentId, choice)
      }
      onHoverChange={(hovered) =>
        setHoveredPackage(hovered ? pkg.packageIndex : null)
      }
      onSelectionChange={(update) => updateSelection(pkg.packageIndex, update)}
      pkg={pkg}
      selection={selections[pkg.packageIndex] ?? defaultSelection(pkg)}
    />
  );

  return (
    <Page>
      <BetaNotice>
        <BetaBadge />
        <span>
          Ruttplaneringen är under utveckling. Granska förslagen innan du
          verkställer, och använd feedbackknappen när något ser fel ut.
          {!canApply &&
            ' Du kan generera och granska förslag, men att verkställa kräver behörighet som sätts under Globala inställningar.'}
        </span>
      </BetaNotice>
      <ControlsRow>
        <Control>
          Datum
          <input
            onChange={(e) => setDateValue(e.currentTarget.value)}
            type="date"
            value={dateValue}
          />
        </Control>
        <Control as="div">
          Affärsenhet
          <BusinessUnitSelect
            onChange={setBusinessUnits}
            value={businessUnits}
          />
        </Control>
        <Control as="div">
          Område
          <MultiSelect
            onChange={(values) => setCounties(values as Set<number>)}
            options={countyOptions}
            placeholder="Alla områden"
            value={counties}
          />
        </Control>
        <Control>
          Anställning
          <Select
            onChange={(e) => {
              const next = e.target.value as EmploymentFilter;
              setEmployment(next);
              // Ett redan gjort fälttestarval kan innehålla någon ur den bortfiltrerade
              // gruppen. Låg den kvar hade planeraren fått en förare som filtret säger nej
              // till, eftersom ett uttryckligt val vinner över anställningsformen.
              setFieldTesterFilter((current) => {
                if (current.size === 0 || next === 'all') return current;
                const allowed = new Set(
                  Object.values(users)
                    .filter((u) =>
                      next === 'fullTime' ? u.isFullTime : !u.isFullTime
                    )
                    .map((u) => u.userID)
                );
                return new Set([...current].filter((id) => allowed.has(id)));
              });
            }}
            small
            value={employment}
          >
            {EMPLOYMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Control>
        <Control as="div">
          Fälttestare
          <MultiSelect
            onChange={(values) => setFieldTesterFilter(values as Set<number>)}
            options={fieldTesterOptions}
            placeholder={
              employment === 'all'
                ? 'Alla fälttestare'
                : `Alla ${employment === 'fullTime' ? 'heltidare' : 'timanställda'} (${fieldTesterOptions.length})`
            }
            value={fieldTesterFilter}
          />
        </Control>
        <Button disabled={isGenerating} onClick={generate} type="button">
          {isGenerating ? 'Genererar…' : 'Generera förslag'}
        </Button>
        {proposal && (
          <Control as="div">
            Sortering
            <Select
              onChange={(e) => setSortMode(e.target.value as SortMode)}
              small
              value={sortMode}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Control>
        )}
        {proposal && proposal.packages.length > 0 && (
          <CollapseAllButton onClick={toggleAllCollapsed} type="button">
            {allCollapsed ? 'Expandera alla' : 'Kollapsa alla'}
          </CollapseAllButton>
        )}
        {proposal && (
          <CollapseAllButton
            onClick={() => setShowOverview((prev) => !prev)}
            type="button"
          >
            {showOverview
              ? 'Dölj hur förslaget togs fram'
              : 'Så togs förslaget fram'}
          </CollapseAllButton>
        )}
        {generatedInfo && <GeneratedInfo>{generatedInfo}</GeneratedInfo>}
      </ControlsRow>

      {isGenerating && (
        <CenteredState>
          <LoadingSpinner>Genererar förslag…</LoadingSpinner>
        </CenteredState>
      )}

      {!isGenerating && proposal && (
        <>
          <StatsBar stats={proposal.stats} />

          {/* Säger ut att prislistan saknas, i stället för att låta "TB okänt" på korten se ut
              som ett planeringsproblem. Ligger ovanför flikarna eftersom det gäller hela
              körningen och påverkar hur korten ska läsas. */}
          <MissingPriceNotice missingPrices={proposal.missingPrices} />

          {showOverview && <RunOverview proposal={proposal} />}

          <Tabs>
            <Tab
              active={activeTab === 'auto'}
              onClick={() => setActiveTab('auto')}
              type="button"
            >
              Redo att tillämpa<span>{partition.auto.length}</span>
            </Tab>
            <Tab
              active={activeTab === 'manual'}
              onClick={() => setActiveTab('manual')}
              type="button"
            >
              Behöver input
              <span>
                {partition.manual.length +
                  // Lagret att flytta väntar inte på något beslut — det räknas inte som input.
                  proposal.unplannableAssignments.filter(
                    (a) =>
                      a.reason !== RoutePlanUnplannableReasonEnum.FlexibleStock
                  ).length}
              </span>
            </Tab>
            <TabsSpacer />
            <MapToggle
              onClick={() => setShowMap((prev) => !prev)}
              type="button"
            >
              {showMap ? 'Dölj karta' : 'Visa karta'}
            </MapToggle>
          </Tabs>

          {/* Båda flikarna ritar SAMMA kort. Ett paket som behöver input är fortfarande ett
              paket, och det input:et efterfrågas — luckväljaren, bytet av fälttestare, ja:et till
              en mellanlandning — är kontroller som sitter på kortet. Den gamla tabellen kunde bara
              namnge orsaken och skicka planeraren någon annanstans för att göra något åt den.
              Skillnaden mellan flikarna ligger i markeringen, inte i formen: defaultSelection
              kryssar bara i automatiska paket, så ingenting här sveper med ett dödläge i apply. */}
          {visiblePackages.length > 0 && (
            <ContentGrid showMap={showMap}>
              <PackageList>
                {packageRows.map((row) =>
                  row.kind === 'single' ? (
                    renderPackageCard(row.pkg)
                  ) : (
                    <SwapGroup key={`swap-${row.swap.swapId}`}>
                      <SwapBracket>
                        <SwapBracketMark>⇄</SwapBracketMark>
                      </SwapBracket>
                      <SwapGroupBody>
                        <SwapGroupHeader>
                          <SwapGroupTitle>
                            Skifte {row.swap.swapId} · {row.swap.pointName}{' '}
                            {formatClock(row.swap.meetingTime)}
                          </SwapGroupTitle>
                          <span>
                            {row.packages.length} ärenden ·{' '}
                            {row.packages.length} förare
                          </span>
                          <SwapGroupSpacer>
                            verkställs tillsammans
                          </SwapGroupSpacer>
                        </SwapGroupHeader>
                        {row.packages.map((pkg) => renderPackageCard(pkg))}
                      </SwapGroupBody>
                    </SwapGroup>
                  )
                )}
              </PackageList>
              {showMap && (
                <MapColumn>
                  <RouteMap
                    hoveredPackageIndex={hoveredPackage}
                    driverSwaps={proposal.driverSwaps}
                    packages={visiblePackages}
                    selections={selections}
                  />
                </MapColumn>
              )}
            </ContentGrid>
          )}

          {activeTab === 'auto' && partition.auto.length === 0 && (
            <CenteredState>
              Inget är redo att tillämpas för valt datum — se fliken Behöver
              input.
            </CenteredState>
          )}

          {/* Uppdrag som ALDRIG blev ett paket: låsta av trafikledaren, utan koordinat, med
              passerad tid. De har varken kandidat, ben eller lucka att välja på, så det finns
              inget kort att rita — där är raden rätt form. */}
          {activeTab === 'manual' && (
            <UnplannableTable
              hasPackagesAbove={partition.manual.length > 0}
              onReserve={reserveAssignment}
              unplannableAssignments={proposal.unplannableAssignments}
            />
          )}

          {/* Styrs av markeringen och inte av att den automatiska listan råkar vara icke-tom.
              applyAll verkställer allt som är ikryssat oavsett flik, så det gamla villkoret gav
              en dag där varje paket behövde input en död återvändsgränd: kryssen gick att sätta
              men baren kom aldrig fram. */}
          {applySummary && applySummary.packageCount > 0 && (
            <ApplyBar
              isApplying={isApplying}
              onApply={applyAll}
              onDeselectAll={deselectAll}
              summary={applySummary}
            />
          )}
        </>
      )}

      {!isGenerating && !proposal && (
        <CenteredState>
          Välj datum och tryck på &quot;Generera förslag&quot; för att paketera
          dagens oplanerade uppdrag i ruttförslag.
        </CenteredState>
      )}

      <FeedbackButton
        onClick={() => openFeedback()}
        raised={
          !isGenerating &&
          !!proposal &&
          !!applySummary &&
          applySummary.packageCount > 0
        }
      />
    </Page>
  );
};

export default RuttplaneringPage;
